import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import StatusPill from "@/components/StatusPill";
import { titleCase } from "@/lib/format";
import { Boxes, Truck, Plus, Upload } from "lucide-react";
import KPICard from "@/components/KPICard";
import { AddInventoryDialog, ImportInventoryDialog } from "@/components/InventoryDialogs";

export default function Inventory() {
    const [items, setItems] = useState([]);
    const [addOpen, setAddOpen] = useState(false);
    const [importOpen, setImportOpen] = useState(false);
    const load = () => api.get("/inventory").then(({ data }) => setItems(data));
    useEffect(() => { load(); }, []);

    const carts = items.filter((i) => i.kind === "cart");
    const containers = items.filter((i) => i.kind === "rolloff_container");
    const deployed = items.filter((i) => i.status === "deployed").length;

    return (
        <div data-testid="inventory-page">
            <PageHeader title="Inventory"
                        subtitle="Carts, containers, lids, wheels and parts. Track every asset and its condition."
                        actions={
                            <div className="flex items-center gap-2">
                                <Button variant="outline" size="sm" onClick={() => setImportOpen(true)}
                                        data-testid="inv-import-btn">
                                    <Upload className="w-3.5 h-3.5 mr-1" />Import CSV
                                </Button>
                                <Button size="sm" onClick={() => setAddOpen(true)}
                                        data-testid="inv-add-btn">
                                    <Plus className="w-3.5 h-3.5 mr-1" />Add cart
                                </Button>
                            </div>
                        } />
            <AddInventoryDialog open={addOpen} onOpenChange={setAddOpen} onCreated={load} />
            <ImportInventoryDialog open={importOpen} onOpenChange={setImportOpen} onImported={load} />
            <div className="px-8 py-6 space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <KPICard testid="inv-kpi-total" label="Total assets" value={items.length} icon={Boxes} />
                    <KPICard testid="inv-kpi-carts" label="Residential carts" value={carts.length} icon={Boxes} />
                    <KPICard testid="inv-kpi-containers" label="Roll-off containers" value={containers.length} icon={Truck} />
                    <KPICard testid="inv-kpi-deployed" label="Deployed" value={deployed} icon={Truck}
                             delta={`${Math.round((deployed/(items.length||1))*100)}% utilization`} deltaTone="success" />
                </div>
                <Card>
                    <div className="overflow-x-auto" data-testid="inventory-table">
                        <table className="w-full text-sm">
                            <thead>
                            <tr className="border-b border-border surface">
                                <th className="text-left px-4 py-3 overline">Asset tag</th>
                                <th className="text-left px-4 py-3 overline">Kind</th>
                                <th className="text-left px-4 py-3 overline">Size</th>
                                <th className="text-left px-4 py-3 overline">Condition</th>
                                <th className="text-left px-4 py-3 overline">Status</th>
                            </tr>
                            </thead>
                            <tbody>
                            {items.map((i) => (
                                <tr key={i.id} className="border-b border-border hover:bg-secondary/40">
                                    <td className="px-4 py-3 font-mono text-xs">{i.asset_tag}</td>
                                    <td className="px-4 py-3">{titleCase(i.kind)}</td>
                                    <td className="px-4 py-3">{i.size}</td>
                                    <td className="px-4 py-3 text-muted-foreground">{titleCase(i.condition)}</td>
                                    <td className="px-4 py-3"><StatusPill status={i.status} /></td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                </Card>
            </div>
        </div>
    );
}
